import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, btnPrimary, btnSecondary, card, EmptyState, InlineError, input, Skeleton, useToast } from '../ui/Kit';
import { Chip, Evidence, fmtDate, fmtDateTime, PrivacyHint, StagePill } from './common';
import { GradeTile, RiskBadge } from './MatchPanel';
import { ScreenLine } from './ApplicationCard';
import { inZone } from './AlexDialogs';
import { RoundsSection } from './InterviewViews';
import { screenLine } from './ScreenDialog';

// The candidate profile's tabs (bundle 4e): Overview, Profile, Screens & interviews, and the activity panel (a side
// panel on wide screens, a tab under 1200 px). Match and Authenticity are the 4b panels.

const box = card + ' p-5 rounded-2xl';
const H = ({ children, right }) => <div className="flex items-center gap-2 mb-2"><h2 className="text-[15px] font-extrabold flex-1">{children}</h2>{right}</div>;
const link = 'text-sm text-[#0066CC] font-semibold hover:underline';

// ── Overview ──
function MatchSummary({ jobId, match, viewOnly, onTab }) {
  if (jobId && viewOnly) return <section className={box}><H>Match</H><p className="text-sm text-[#64748B]" data-testid="overview-view-only">View only — you’re not on this job, so its match isn’t shown.</p></section>;
  if (!jobId) return <section className={box}><H>Match</H><p className="text-sm text-[#64748B]">Not on a job yet — add them to one to see how they match.</p></section>;
  if (!match) return <section className={box + ' min-h-[112px] space-y-2'} aria-busy="true"><Skeleton className="h-5 w-1/3" /><Skeleton /><Skeleton className="h-4 w-1/2" /></section>;
  const musts = (match.must_haves || []).filter((m) => m.on !== false);
  const met = musts.filter((m) => m.result === 'pass').length;
  const done = match.status === 'done';
  return (
    <section className={box + ' min-h-[112px]'} data-testid="overview-match">
      <div className="flex items-start gap-3">
        {done && <GradeTile grade={match.grade} total={match.total} />}
        <div className="flex-1 min-w-0">
          <h2 className="text-[15px] font-extrabold">{done ? `Why ${match.grade} · ${Math.round(match.total)}%` : `Match · ${(match.job || {}).title || ''}`}</h2>
          {done ? <p className="text-sm text-[#334155] mt-0.5"><Evidence text={match.why} quote={false} max={220} /></p>
            : <p className="text-sm text-[#64748B] mt-0.5">{match.status === 'not_scored' ? 'Doesn’t meet every must-have — not AI-scored' : match.status === 'failed' ? 'Scoring failed — it retries on its own' : 'Scoring…'}</p>}
        </div>
      </div>
      {musts.length > 0 && (
        <button type="button" className={link + ' mt-2'} onClick={() => onTab('match')} data-testid="overview-musts">
          Must-haves {met === musts.length ? '✓' : '✗'} {met}/{musts.length} →</button>
      )}
    </section>
  );
}

function KeyFacts({ facts }) {
  return (
    <section className={box}>
      <H>Key facts</H>
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-[13px]" data-testid="key-facts">
        {(facts || []).map((f) => (
          <div key={f.key} data-testid={`fact-${f.key}`}><dt className="text-[#64748B]">{f.label}</dt>
            <dd className="font-semibold" title={f.hidden ? 'Only the job’s recruiters, its hiring manager and admins see it' : undefined}>{f.text || '—'}</dd></div>
        ))}
      </dl>
    </section>
  );
}

function ScreenSummary({ app, onOpenScreen }) {
  const sm = app && app.screen_summary;
  return (
    <section className={box}>
      <H right={app && onOpenScreen && <button type="button" className={link} onClick={onOpenScreen} data-testid="overview-open-screen">{sm && sm.saved_at ? 'Open screen' : 'Screen candidate'}</button>}>Recruiter screen</H>
      {!app ? <p className="text-sm text-[#64748B]">Not on a job yet.</p>
        : sm && sm.saved_at ? <>
          <p className="text-sm" data-testid="overview-screen">{screenLine(sm)}</p>
          {sm.notes && <p className="text-sm text-[#475569] mt-1">{sm.notes}</p>}
        </> : <p className="text-sm text-[#64748B]">Not screened yet.</p>}
    </section>
  );
}

function RiskSummary({ a, onTab }) {
  if (!a) return null;
  return (
    <section className={box} data-testid="overview-risk">
      <H right={<button type="button" className={link} onClick={() => onTab('authenticity')}>Authenticity →</button>}>Risk</H>
      <div className="flex items-center gap-2 flex-wrap"><RiskBadge risk={a.risk} />
        <span className="text-sm text-[#334155]">{a.needs_verification ? `${a.needs_verification} item${a.needs_verification === 1 ? '' : 's'} to verify` : 'Nothing to verify'}</span></div>
      {(a.reasons || []).length > 0 && <ul className="list-disc pl-5 mt-2 text-sm text-[#7F1D1D]">{a.reasons.slice(0, 3).map((r) => <li key={r}>{r}</li>)}</ul>}
    </section>
  );
}

function OtherJobs({ apps, current, hidden, onJob }) {
  const others = apps.filter((a) => !current || a.id !== current.id);
  return (
    <section className={box}>
      <H>Other jobs</H>
      {!others.length && !hidden ? <p className="text-sm text-[#64748B]">No other jobs.</p> : (
        <ul className="divide-y divide-[#EEF1F6]" data-testid="other-jobs">
          {others.map((a) => (
            <li key={a.id} className="py-2 flex items-center gap-2 text-sm">
              <button type="button" className="font-semibold text-left flex-1 min-w-0 truncate hover:text-[#0066CC]" onClick={() => onJob(a.requirement_id)}>{a.job_title}</button>
              <StagePill stage={a.stage} status={a.status} round={a.interview_round} />
            </li>
          ))}
          {hidden > 0 && <li className="py-2 text-sm text-[#64748B]" data-testid="other-jobs-hidden">{hidden} other job{hidden > 1 ? 's' : ''} (no access)</li>}
        </ul>
      )}
    </section>
  );
}

export function OverviewTab({ c, jobId, app, match, viewOnly, apps, hidden, onTab, onJob, onOpenScreen }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4 items-start" data-testid="tab-overview">
      <div className="space-y-4"><MatchSummary jobId={jobId} match={match} viewOnly={viewOnly} onTab={onTab} /><KeyFacts facts={c.key_facts} />
        <ScreenSummary app={app} onOpenScreen={onOpenScreen} /></div>
      <div className="space-y-4"><RiskSummary a={c.authenticity} onTab={onTab} /><OtherJobs apps={apps} current={app} hidden={hidden} onJob={onJob} /></div>
    </div>
  );
}

// ── Profile ──
export function ParsedProfile({ c }) {
  const p = c.profile;
  if (!p) return <EmptyState title="No parsed profile yet" hint="It appears once a resume has been read." />;
  const skills = c.skills || [];            // candidate_skills — the same rows the match panel reads
  return (
    <div className="space-y-4 text-sm">
      {p.summary && <p className="text-[#334155]">{p.summary}</p>}
      {(p.experiences || []).length > 0 && (
        <div><h4 className="font-bold mb-1.5">Experience</h4>
          <ol className="space-y-3 border-l-2 border-[#E6E9F0] pl-4">
            {p.experiences.map((e, i) => (
              <li key={i}><p className="font-semibold">{e.title}{e.company ? ` · ${e.company}` : ''}</p>
                <p className="text-xs text-[#64748B]">{[e.start, e.current ? 'present' : e.end].filter(Boolean).join(' – ')}{e.location ? ` · ${e.location}` : ''}</p>
                {e.description && <p className="text-[#334155] mt-0.5">{e.description}</p>}
                {(e.skills || []).length > 0 && <p className="text-xs text-[#64748B] mt-0.5">{e.skills.join(', ')}</p>}</li>
            ))}
          </ol></div>
      )}
      {skills.length > 0 && (
        <div><h4 className="font-bold mb-1.5">Skills</h4><div className="flex flex-wrap gap-1.5" data-testid="skill-chips">
          {skills.slice(0, 40).map((s) => <Chip key={s.canonical} tone={s.listed_only ? 'warn' : 'plain'} title={s.evidence || undefined}>{s.label}</Chip>)}</div></div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {(p.education || []).length > 0 && <div><h4 className="font-bold mb-1">Education</h4>{p.education.map((e, i) => <p key={i}>{[e.degree, e.field].filter(Boolean).join(' ')}{e.institution ? `, ${e.institution}` : ''}{e.end ? ` (${e.end})` : ''}</p>)}</div>}
        {(p.certifications || []).length > 0 && <div><h4 className="font-bold mb-1">Certifications</h4><p>{p.certifications.map((x) => (typeof x === 'string' ? x : x.name)).join(', ')}</p></div>}
        {(p.domains || []).length > 0 && <div><h4 className="font-bold mb-1">Domains</h4><p>{p.domains.join(', ')}</p></div>}
        {p.management && (p.management.max_team || p.management.current_team) && <div><h4 className="font-bold mb-1">Management</h4><p>Leads {p.management.current_team || p.management.max_team}{p.management.years ? ` · ${p.management.years} yrs managing` : ''}</p></div>}
      </div>
    </div>
  );
}

function ResumeViewer({ c }) {
  const toast = useToast();
  const docs = c.documents || [];
  const [docId, setDocId] = useState(docs.length ? docs[0].id : null);
  const [url, setUrl] = useState(null);
  const doc = docs.find((d) => d.id === docId);
  const isPdf = !!doc && /\.pdf$/i.test(doc.filename || '');
  useEffect(() => {
    setUrl(null);
    if (!doc || !isPdf) return;
    api(`/api/candidates/${c.id}/documents/${doc.id}/download?inline=true`).then((d) => setUrl(d.url)).catch(() => setUrl(false));
  }, [c.id, docId]); // eslint-disable-line react-hooks/exhaustive-deps
  const download = (d) => api(`/api/candidates/${c.id}/documents/${d.id}/download`).then((x) => { window.location.href = x.url; }).catch((e) => toast.show(e.message, 'error'));
  if (!docs.length) return <section className={box}><H>Resume</H><p className="text-sm text-[#64748B]">No documents.</p></section>;
  return (
    <section className={box} data-testid="resume-viewer">
      <H right={doc && <button type="button" className={link} onClick={() => download(doc)}>Download</button>}>Resume</H>
      {docs.length > 1 && (
        <select className={input + ' mb-2'} aria-label="Document" value={docId} onChange={(e) => setDocId(Number(e.target.value))}>
          {docs.map((d) => <option key={d.id} value={d.id}>{d.filename || 'Resume'} · {fmtDate(d.created_at)}</option>)}</select>
      )}
      <div className="h-[70vh] min-h-[420px] rounded-lg border border-[#E6E9F0] bg-[#F8FAFC] overflow-hidden">
        {!isPdf ? <p className="text-sm text-[#64748B] p-4">The preview shows PDFs only — download {doc.filename || 'the file'} to read it.</p>
          : url === null ? <div className="p-4 space-y-2" aria-busy="true"><Skeleton className="h-4 w-2/3" /><Skeleton /><Skeleton /></div>
            : url === false ? <p className="text-sm text-[#64748B] p-4">The preview couldn’t be loaded — use Download.</p>
              : <iframe title="Resume" src={url} className="w-full h-full" data-testid="resume-frame" />}
      </div>
    </section>
  );
}

export function ProfileTab({ c }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4 items-start" data-testid="tab-profile">
      <ResumeViewer c={c} />
      <div className="space-y-4">
        <section className={box}><H>Parsed profile</H><ParsedProfile c={c} /></section>
        <section className={box}>
          <H>Talent pool & tags</H>
          <div className="flex flex-wrap gap-1.5">{c.talent_pool ? <Chip tone="yes">In the talent pool</Chip> : <Chip>Not in the talent pool</Chip>}
            {(c.tags || []).map((t) => <Chip key={t}>{t}</Chip>)}</div>
        </section>
      </div>
    </div>
  );
}

// ── Screens & interviews ──
const ROUND_STATE = { done: 'Passed', out: 'Out', locked: 'Not scheduled' };

const ALEX_TONE = { invited: 'bg-[#E0F2FE] text-[#075985]', accepted: 'bg-[#DCFCE7] text-[#166534]', in_progress: 'bg-[#EDE9FE] text-[#5B21B6]',
  completed: 'bg-[#DCFCE7] text-[#166534]', cancelled: 'bg-[#F1F5F9] text-[#475569]', no_show: 'bg-[#FEE2E2] text-[#991B1B]' };
const viewerZone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return 'UTC'; } };

function AlexCard({ app, onAlex }) {
  const navigate = useNavigate();
  const ax = app.alex;
  const can = app.alex_actions || {};
  const done = ax && ax.status_key === 'completed';
  return (
    <section className={box} data-testid="alex-screen">
      <H right={ax && <button type="button" className={link} onClick={() => navigate(ax.link)}>Open the AI screen report →</button>}>Quali5d AI screen (Alex)</H>
      {!ax ? <div className="flex flex-wrap items-center gap-3"><p className="text-sm text-[#64748B] flex-1">Not scheduled.</p>
        {can.schedule && onAlex && <button className={btnPrimary + ' text-xs py-1.5'} onClick={() => onAlex('alex')} data-testid="alex-card-schedule">Schedule Alex screen</button>}</div> : <>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${ALEX_TONE[ax.status_key] || 'bg-[#F1F5F9] text-[#334155]'}`} data-testid="alex-status">{ax.status}</span>
          {ax.duration_minutes && <span className="text-xs text-[#64748B]">{ax.duration_minutes} min</span>}
        </div>
        <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
          <div><dt className="text-[#64748B]">Your time</dt><dd className="font-semibold" data-testid="alex-time-viewer">{inZone(ax.scheduled_at, viewerZone())}</dd></div>
          <div><dt className="text-[#64748B]">Candidate’s time</dt><dd className="font-semibold" data-testid="alex-time-candidate">{inZone(ax.scheduled_at, ax.timezone || 'UTC')}</dd></div>
          {done && <div><dt className="text-[#64748B]">Score</dt><dd className="font-semibold" data-testid="alex-score">{ax.score != null ? `${Math.round(ax.score)} / ${ax.score_max}` : '—'}{ax.recommendation ? ` · ${ax.recommendation}` : ''}</dd></div>}
          <div><dt className="text-[#64748B]">HM decision</dt><dd className="font-semibold">{ax.hm_decision || 'Waiting'}</dd></div>
        </dl>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {done && ax.video_link && <button type="button" className={link} onClick={() => navigate(ax.video_link)} data-testid="alex-video">Watch the video →</button>}
          <span className="flex-1" />
          {can.reschedule && onAlex && <button className={btnSecondary + ' text-xs py-1.5'} onClick={() => onAlex('alex_reschedule')} data-testid="alex-reschedule">Reschedule</button>}
          {can.cancel && onAlex && <button className={btnSecondary + ' text-xs py-1.5 text-red-700'} onClick={() => onAlex('alex_cancel')} data-testid="alex-cancel">Cancel</button>}
          {can.move && onAlex && <button type="button" className="text-xs text-[#64748B] hover:underline" onClick={() => onAlex('alex_move')} data-testid="alex-move">Move this Alex interview to another candidate…</button>}
        </div>
      </>}
    </section>
  );
}

export function ScreensTab({ app, onOpenScreen, onAlex }) {
  if (!app) return <div className={box}><EmptyState title="Not on a job yet" hint="Screens and interviews belong to a job." /></div>;
  const rounds = ((app.progress && app.progress.steps) || []).filter((s) => s.key.startsWith('interview'));
  return (
    <div className="space-y-4" data-testid="tab-screens">
      <section className={box}>
        <H right={onOpenScreen && <button type="button" className={link} onClick={onOpenScreen}>{app.screen_summary && app.screen_summary.saved_at ? 'Open screen' : 'Screen candidate'}</button>}>Recruiter screen</H>
        {app.screen_summary && app.screen_summary.saved_at ? <ScreenLine app={app} onOpen={onOpenScreen} /> : <p className="text-sm text-[#64748B]">Not screened yet.</p>}
      </section>
      <AlexCard app={app} onAlex={onAlex} />
      <section className={box}>
        <H>Interview rounds</H>
        <RoundsSection app={app} />
        <p className="text-xs font-semibold text-[#64748B] mt-3 mb-1">The plan</p>
        {!rounds.length ? <p className="text-sm text-[#64748B]">No interview plan on this job.</p> : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="interview-rounds">
            {rounds.map((r) => (
              <li key={r.key} className="rounded-xl border border-[#E6E9F0] px-3 py-2.5 text-sm" data-testid={`round-${r.key}`}>
                <b>Round {r.label.replace(/^R/, '')}</b>
                <span className="block text-[#64748B]">{r.state === 'current' ? (r.sub || 'Current') : ROUND_STATE[r.state]}</span></li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ── Activity ──
const KIND_ICON = { call: '📞', email: '✉️', note: '📝', stage: '➜', ownership: '👤', import: '⤓', apply: '📨', merge: '⇄', transfer: '⇆',
  update: '✎', export: '⇪', reminder: '⏰' };
const FILTERS = [['all', 'All', null], ['notes', 'Notes', 'note'], ['calls', 'Calls', 'call'], ['emails', 'E-mails', 'email'], ['stage', 'Stage changes', 'stage']];

export function ActivityPanel({ c, appId, onAdded }) {
  const toast = useToast();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [f, setF] = useState('all');
  const kind = (FILTERS.find((x) => x[0] === f) || [])[2];
  const rows = (c.activities || []).filter((x) => !kind || x.kind === kind);
  const save = async () => {
    setBusy(true); setError('');
    try {
      await api(`/api/candidates/${c.id}/activities`, { method: 'POST', body: { kind: 'note', body: body.trim(), application_id: appId || null } });
      setBody(''); toast.show('Note added'); onAdded && onAdded();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <section className={box} data-testid="activity-panel">
      <H>Activity</H>
      <label className="block"><span className="sr-only">Add a note</span>
        <textarea className={input} rows={3} value={body} maxLength={5000} placeholder="Add a note…" onChange={(e) => setBody(e.target.value)} data-testid="note-composer" /></label>
      <PrivacyHint />
      <div className="flex justify-end mt-2"><button className={btnPrimary + ' text-xs py-1.5'} disabled={busy || !body.trim()} onClick={save} data-testid="note-save">Add note</button></div>
      <InlineError>{error}</InlineError>
      <div className="flex flex-wrap gap-1.5 mt-3" role="group" aria-label="Show">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" aria-pressed={f === k} onClick={() => setF(k)} data-testid={`filter-${k}`}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${f === k ? 'border-[#0066CC] bg-[#0066CC] text-white' : 'border-[#E6E9F0] bg-white text-[#334155]'}`}>{l}</button>
        ))}
      </div>
      <ol className="space-y-3 mt-3" data-testid="activity">
        {rows.map((x) => (
          <li key={x.id} className="flex gap-3 text-sm">
            <span aria-hidden="true" className="w-6 text-center">{KIND_ICON[x.kind] || '•'}</span>
            <span className="flex-1 min-w-0"><span className="text-[#0F172A] break-words">{x.body || 'Activity'}</span>
              <span className="block text-xs text-[#64748B]">{x.user || 'Quali5d'} · {fmtDateTime(x.occurred_at)}{x.duration_s ? ` · ${Math.round(x.duration_s / 60)} min` : ''}</span></span>
          </li>))}
        {!rows.length && <li className="text-sm text-[#64748B]">Nothing here yet.</li>}
      </ol>
    </section>
  );
}

